"use client";

/**
 * Shared link-type resolution for SmartButton/SmartLink, so the href → behavior mapping lives in
 * one place instead of being independently duplicated across both components (which is how v1's
 * and v2's smart-link systems already drifted from each other).
 */

import { useMemo } from "react";
import {
  parseEmailLink,
  parseFormLink,
  parseHotelBookingLink,
  isAnchorLink,
  isExternalLink,
  isUnsafeLinkScheme,
} from "../_logic/smart-link-utils";

export type SmartLinkTarget =
  | { kind: "unsafe" }
  | { kind: "email"; templateId: number }
  | { kind: "form"; formId: number; inviteToken: string | null }
  | { kind: "hotel-booking"; bookingLinkId: number }
  | { kind: "anchor" }
  | { kind: "external" }
  | { kind: "internal" };

/**
 * Pure resolver — call directly outside a component, or via {@link useSmartLinkTarget} inside one.
 * @param href - The link to resolve
 * @param eventId - Current event id, for scoping form/hotel-booking link matches
 * @returns The resolved link target
 */
export function resolveSmartLinkTarget(href: string, eventId: number): SmartLinkTarget {
  if (isUnsafeLinkScheme(href)) return { kind: "unsafe" };

  // Must run before the anchor check: an email link starts with `#` and would otherwise resolve as one.
  const templateId = parseEmailLink(href);
  if (templateId !== null) return { kind: "email", templateId };

  const { isFormLink, formId, inviteToken } = parseFormLink(href, eventId);
  if (isFormLink && formId) return { kind: "form", formId, inviteToken };

  const { isHotelBookingLink, bookingLinkId } = parseHotelBookingLink(href, eventId);
  if (isHotelBookingLink && bookingLinkId) return { kind: "hotel-booking", bookingLinkId };

  if (isAnchorLink(href)) return { kind: "anchor" };
  if (isExternalLink(href)) return { kind: "external" };
  return { kind: "internal" };
}

/**
 * Memoized hook form of {@link resolveSmartLinkTarget}. Effect-free and pure — SmartButton/SmartLink
 * render on every public LP CTA, so this must never trigger a network call or a state update.
 * @param href - The link to resolve
 * @param eventId - Current event id, for scoping form/hotel-booking link matches
 * @returns The resolved link target
 */
export function useSmartLinkTarget(href: string, eventId: number): SmartLinkTarget {
  return useMemo(() => resolveSmartLinkTarget(href, eventId), [href, eventId]);
}
